import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ListBulletIcon } from "@heroicons/react/24/outline";
import {
  formatGroupLabel,
  numberForFlatGroupRows,
} from "./utils/offer-group-hierarchy";
import {
  buildOfferLinesDisplayRows,
  DEFAULT_OFFER_LINES_VIEW_MODE,
} from "./utils/offer-lines-view";
import { OfferViewportSummary } from "./offer-viewport-summary";
import { PromangRichTextContent } from "../../utils/PromangRichTextContent";
import { safeHttpUrl } from "../../utils/sanitize-rich-html";

const ACCENT = "#1463C7";

export function formatMoney(value, currency = "NOK") {
  const n = Number(value);
  if (!Number.isFinite(n)) {
    return new Intl.NumberFormat("nb-NO", {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
    }).format(0);
  }
  return new Intl.NumberFormat("nb-NO", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
  }).format(n);
}

export function formatQty(value) {
  if (value === null || value === undefined || value === "") return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return new Intl.NumberFormat("nb-NO", {
    maximumFractionDigits: 2,
  }).format(n);
}

export function unitLabel(item) {
  const unit = item?.unit;
  if (!unit) return "—";
  if (typeof unit === "string") return unit;
  return (
    unit.short_title ||
    unit.name ||
    unit.title ||
    unit.symbol ||
    unit.abbreviation ||
    "—"
  );
}

export function chapterLabel(item, isGroup) {
  if (item?._ungrouped) {
    return { primary: null, secondary: null, ungrouped: true };
  }
  const title = (item?.title || "").trim();
  const description = (item?.description || "").trim();
  if (isGroup) {
    const primary = formatGroupLabel(
      {
        title: title || item?.discipline,
        number: item?.number,
        discipline: item?.discipline,
      },
      title || description || "—"
    );
    return {
      primary,
      secondary: description && description !== title ? description : null,
    };
  }
  if (title && description) return { primary: title, secondary: description };
  if (title) return { primary: title, secondary: null };
  if (description) return { primary: description, secondary: null };
  return { primary: "—", secondary: null };
}

export function isGroupRow(item) {
  const type = item?.item_type || item?.type;
  if (type === "group" || item?.group_row === true) return true;
  // Explicit line types stay product rows even when quantity is blank in the editor.
  if (["product", "manual", "hours", "calculation", "text"].includes(type)) {
    return false;
  }
  const qty = item?.quantity;
  if (qty === null || qty === undefined || qty === "") return true;
  return false;
}

/** Module / customer note rows (item_type text) — not priced lines. */
export function isTextRow(item) {
  const type = item?.item_type || item?.type;
  return type === "text";
}

/** null/undefined treated as visible (customer-facing default). */
export function isVisibleToCustomer(item) {
  return item?.visible_to_customer !== false;
}

/** Sales line total — mirrors Offer::Item#total_amount (surcharge then discount). */
export function customerLineSum(item) {
  if (isGroupRow(item) || isTextRow(item)) return null;
  const qty = parseFloat(item?.quantity ?? 0);
  const price = parseFloat(item?.sales_price ?? 0);
  if (!Number.isFinite(qty) || !Number.isFinite(price)) return 0;
  let total = price * qty;
  const surcharge = parseFloat(item?.surcharge ?? 0);
  const discount = parseFloat(item?.discount ?? 0);
  if (Number.isFinite(surcharge) && surcharge > 0) {
    total += (total * surcharge) / 100;
  }
  if (Number.isFinite(discount) && discount > 0) {
    total -= (total * discount) / 100;
  }
  return total;
}

/**
 * Lightweight customer-facing offer lines table.
 * Claude: white bordered card, uppercase muted headers, Synlig checkbox.
 *
 * viewMode default `kapittel` = Offer::Group structure (calc headers as fallback).
 */
export default function OfferCustomerLines({
  items = [],
  groups = [],
  viewMode = DEFAULT_OFFER_LINES_VIEW_MODE,
  currency = "NOK",
  onVisibilityChange,
}) {
  const { t } = useTranslation();
  const displayRows = useMemo(
    () =>
      buildOfferLinesDisplayRows({
        items,
        groups,
        viewMode,
      }),
    [items, groups, viewMode]
  );
  const visibleTotal = useMemo(
    () =>
      displayRows.reduce((acc, row) => {
        if (row.isChapterHeader || isGroupRow(row.item) || isTextRow(row.item)) return acc;
        if (!isVisibleToCustomer(row.item) || ["optional", "excluded"].includes(row.item.pricing_role)) return acc;
        const sum = customerLineSum(row.item);
        if (sum === null || !Number.isFinite(sum)) return acc;
        return acc + sum;
      }, 0),
    [displayRows]
  );

  if (!items.length) {
    return (
      <div className="flex h-full min-h-0 flex-col items-center justify-center gap-2 rounded-[10px] border border-dashed border-slate-300 bg-slate-50 px-6 py-14 text-center">
        <ListBulletIcon className="h-8 w-8 text-slate-300" aria-hidden="true" />
        <p className="text-sm font-medium text-slate-700">
          {t("offers.tabs.lines_empty_title")}
        </p>
        <p className="max-w-md text-sm text-slate-500">
          {t("offers.tabs.lines_empty_desc")}
        </p>
      </div>
    );
  }

  return (
    <div className="offer-customer-lines flex h-full min-h-0 flex-col overflow-hidden bg-white">
      <div className="min-h-0 flex-1 overflow-auto">
        <table className="min-w-full border-collapse text-[12.5px]">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              <th
                scope="col"
                className="px-3 py-2 text-left text-[10.5px] font-medium uppercase tracking-wide text-slate-400"
              >
                {t("offers.tabs.columns.chapter")}
              </th>
              <th
                scope="col"
                className="px-3 py-2 text-right text-[10.5px] font-medium uppercase tracking-wide text-slate-400"
              >
                {t("offers.tabs.columns.qty")}
              </th>
              <th
                scope="col"
                className="px-3 py-2 text-left text-[10.5px] font-medium uppercase tracking-wide text-slate-400"
              >
                {t("offers.tabs.columns.unit")}
              </th>
              <th
                scope="col"
                className="px-3 py-2 text-right text-[10.5px] font-medium uppercase tracking-wide text-slate-400"
              >
                {t("offers.tabs.columns.unit_price")}
              </th>
              <th
                scope="col"
                className="px-3 py-2 text-right text-[10.5px] font-medium uppercase tracking-wide text-slate-400"
              >
                {t("offers.tabs.columns.line_sum")}
              </th>
              <th
                scope="col"
                className="px-3 py-2 text-center text-[10.5px] font-medium uppercase tracking-wide text-slate-400"
              >
                {t("offers.tabs.columns.visible")}
              </th>
            </tr>
          </thead>
          <tbody>
            {displayRows.map((row) => {
              const item = row.item;
              const group = row.isChapterHeader;
              const note = !group && isTextRow(item);
              const { primary, secondary, ungrouped } = chapterLabel(
                item,
                group
              );
              const sum = group || note ? null : customerLineSum(item);
              const visible =
                item?.visible_to_customer === undefined ||
                item?.visible_to_customer === null
                  ? true
                  : Boolean(item.visible_to_customer);
              const padLeft = 12 + (row.depth || 0) * 14;

              return (
                <tr
                  key={row.key}
                  className={
                    group
                      ? "border-b border-slate-100 bg-slate-50/60"
                      : note
                        ? "border-b border-slate-100 bg-slate-50/30"
                        : visible
                          ? "border-b border-slate-100"
                          : "border-b border-slate-100 opacity-60"
                  }
                >
                  <td
                    className="px-3 py-2"
                    style={{ paddingLeft: padLeft }}
                    colSpan={note ? 5 : undefined}
                  >
                    <div
                      className={
                        group
                          ? "font-semibold text-slate-900"
                          : note
                            ? "font-medium text-slate-700"
                            : "font-normal text-slate-800"
                      }
                    >
                      {ungrouped ? (
                        t("offers.tabs.lines_view.ungrouped")
                      ) : note ? (
                        <PromangRichTextContent
                          html={
                            item?.description ||
                            item?.title ||
                            primary ||
                            ""
                          }
                          className="text-sm text-slate-700"
                        />
                      ) : (
                        primary
                      )}
                    </div>
                    {!note && secondary ? (
                      <div className="mt-0.5 text-[11px] text-slate-500">
                        {secondary}
                      </div>
                    ) : null}
                    {!group && !note && safeHttpUrl(item?.product_image_url) ? (
                      <div className="mt-1.5">
                        <img
                          src={safeHttpUrl(item.product_image_url)}
                          alt=""
                          className="max-h-20 max-w-[100px] object-contain"
                        />
                      </div>
                    ) : null}
                    {!group && !note && item?.product_short_description ? (
                      <div className="mt-0.5 text-[11px] text-slate-500">
                        {item.product_short_description}
                      </div>
                    ) : null}
                    {!group && !note && safeHttpUrl(item?.product_url) ? (
                      <div className="mt-0.5 text-[11px]">
                        <a
                          href={safeHttpUrl(item.product_url)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#1463C7] underline-offset-2 hover:underline"
                        >
                          {item.product_url}
                        </a>
                      </div>
                    ) : null}
                  </td>
                  {note ? null : (
                    <>
                      <td className="px-3 py-2 text-right tabular-nums text-slate-700">
                        {group || item?._hide_quantity ? "" : formatQty(item?.quantity)}
                      </td>
                      <td className="px-3 py-2 text-slate-400">
                        {group || item?._hide_unit ? "" : unitLabel(item)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-slate-700">
                        {group || item?._hide_unit_price
                          ? ""
                          : formatMoney(item?.sales_price, currency)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums font-semibold text-slate-900">
                        {sum === null ? "" : formatMoney(sum, currency)}
                      </td>
                    </>
                  )}
                  <td className="px-3 py-2 text-center">
                    {group || row.itemIndex == null ? null : (
                      <input
                        type="checkbox"
                        checked={visible}
                        onChange={(e) =>
                          onVisibilityChange?.(row.itemIndex, e.target.checked)
                        }
                        className="h-4 w-4 rounded border-slate-300"
                        style={{ accentColor: ACCENT }}
                        aria-label={t("offers.tabs.columns.visible")}
                      />
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <OfferViewportSummary>
      <div className="offer-lines-summary flex shrink-0 items-center justify-end gap-3 border-t border-slate-200 bg-slate-50 px-3 py-2">
        <span className="text-[10.5px] font-medium uppercase tracking-wide text-slate-400">
          {t("offers.printLinesPreview.total")}
        </span>
        <span className="text-[13px] font-semibold tabular-nums text-slate-900">
          {formatMoney(visibleTotal, currency)}
        </span>
      </div>
      </OfferViewportSummary>
    </div>
  );
}

// Re-export for callers that still number flat rows themselves.
export { numberForFlatGroupRows };
